import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { answeredCount, likelihood } from './scoring';
import { TAnswers } from './types';

export type TOutcomeKey =
  | 'highly_likely'
  | 'likely'
  | 'uncertain'
  | 'unlikely'
  | 'incomplete';

type TOutcome = {
  key: TOutcomeKey;
  chip: MessageDescriptor;
  icon: string;
  accent: string;
  title: MessageDescriptor;
  body: MessageDescriptor;
  points: MessageDescriptor[];
  cta: MessageDescriptor;
};

export const OUTCOMES: Record<TOutcomeKey, TOutcome> = {
  highly_likely: {
    key: 'highly_likely',
    chip: msg`Excellent prospects`,
    icon: 'trophy',
    accent: 'green',
    title: msg`Your company is a great match for an iwoca loan`,
    body: msg`There's a lot to suggest an iwoca loan is a good fit for you. We make the final decision individually, as soon as we have your complete application.`,
    cta: msg`Apply for a loan now`,
    points: [
      msg`Your profile matches what many iwoca customers bring to the table.`,
      msg`An application is non-binding and free of charge.`,
      msg`The final approval follows a review of your complete application.`,
    ],
  },
  likely: {
    key: 'likely',
    chip: msg`Good prospects`,
    icon: 'spark',
    accent: 'denim',
    title: msg`A lot points to an iwoca loan being a good fit`,
    body: msg`Your details suggest an iwoca loan could be a good fit for you. We'll clarify a few details together during the application.`,
    cta: msg`Start application`,
    points: [
      msg`Your details paint a promising picture.`,
      msg`During the application we review everything individually and in full.`,
      msg`You take on no obligation by applying.`,
    ],
  },
  uncertain: {
    key: 'uncertain',
    chip: msg`Individual review`,
    icon: 'compass',
    accent: 'amber',
    title: msg`Your case is well suited to a personal assessment`,
    body: msg`Based on your details, it's not yet possible to say for certain how well an iwoca loan fits — that's best clarified in a personal review. Simply submit a non-binding application.`,
    cta: msg`Submit application`,
    points: [
      msg`Every application at iwoca is reviewed individually.`,
      msg`During the application you can add further details.`,
      msg`The application is free of charge and non-binding.`,
    ],
  },
  unlikely: {
    key: 'unlikely',
    chip: msg`Still open for now`,
    icon: 'heart',
    accent: 'neutral',
    title: msg`An iwoca loan is still hard to assess right now`,
    body: msg`Based on your details so far, the assessment is still cautious. This isn't a decision — every application at iwoca is reviewed individually. You're welcome to submit a non-binding application.`,
    cta: msg`Learn more`,
    points: [
      msg`This isn't a rejection — we review every case personally.`,
      msg`If your situation changes, you're welcome any time.`,
      msg`Start over to run the check again with different details.`,
    ],
  },
  incomplete: {
    key: 'incomplete',
    chip: msg`No details yet`,
    icon: 'info',
    accent: 'neutral',
    title: msg`Answer a question to see an assessment`,
    body: msg`You skipped every question, so there's nothing to assess yet. Start over to answer a few and see an initial, non-binding assessment — or submit an application anyway.`,
    cta: msg`Submit an application anyway`,
    points: [
      msg`The more you provide, the more accurate the assessment.`,
      msg`Your data stays non-binding and is treated confidentially.`,
    ],
  },
};

const HIGHLY_LIKELY_METER_MIN = 75;
const LIKELY_METER_MIN = 55;
const UNCERTAIN_METER_MIN = 35;

export const evaluate = (
  answers: TAnswers,
): { key: TOutcomeKey; meter: number } => {
  if (answeredCount(answers) === 0) return { key: 'incomplete', meter: 0 };
  const meter = likelihood(answers);
  let key: TOutcomeKey;
  if (meter >= HIGHLY_LIKELY_METER_MIN) key = 'highly_likely';
  else if (meter >= LIKELY_METER_MIN) key = 'likely';
  else if (meter >= UNCERTAIN_METER_MIN) key = 'uncertain';
  else key = 'unlikely';
  return { key, meter };
};
